/**
 * AI Activity API Service
 * Fetches AI agent interaction logs for transparency/audit trail.
 */

import { api } from '../utils/api';

export interface AIActivityEntry {
  activity_id: string;
  report_id: string;
  agent_name: string;
  action: string;
  input_summary: string;
  output_summary: string;
  model: string;
  tokens_used: number;
  duration_ms: number;
  cost_estimate: number;
  triggered_by: string;
  status: string;
  error_message?: string;
  metadata?: Record<string, any>;
  created_at: string;
}

export interface AIActivitySummary {
  report_id: string;
  total_interactions: number;
  total_tokens: number;
  total_cost: number;
  error_count: number;
  agents: Record<string, {
    count: number;
    total_tokens: number;
    total_duration_ms: number;
    total_cost: number;
    last_activity: string;
  }>;
}

/** Get AI activity timeline for a specific case. */
export async function getCaseAIActivity(
  reportId: string,
  options?: { agentName?: string; limit?: number; offset?: number }
): Promise<AIActivityEntry[]> {
  const params = new URLSearchParams();
  if (options?.agentName) params.set('agent_name', options.agentName);
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.offset) params.set('offset', String(options.offset));
  const qs = params.toString();
  const res = await api.get(`/api/v1/ai-activity/${reportId}${qs ? `?${qs}` : ''}`);
  return res.entries || [];
}

/** Get AI activity summary for a case (agent breakdown, totals). */
export async function getCaseAISummary(reportId: string): Promise<AIActivitySummary> {
  return api.get(`/api/v1/ai-activity/${reportId}/summary`);
}

/** Get recent AI activity across all cases (dashboard card). */
export async function getRecentAIActivity(limit = 20): Promise<AIActivityEntry[]> {
  const res = await api.get(`/api/v1/ai-activity/recent/all?limit=${limit}`);
  return res.entries || [];
}
